/*
 * Guided Tour — LEO onboarding bubbles.
 *
 * Static, hand-written copy (see TOUR_STEPS below) — NOT AI-generated.
 * Two tours (both bilingual PL/EN, dark/gold aesthetic):
 *   • "dashboard" — steps 1-5 + 8. Auto-fires on the FIRST visit to /dashboard
 *     when localStorage has no `leo_tour_dashboard_done_v1` for this user.
 *   • "crm" — steps 6-7. Auto-fires on the FIRST visit to /crm when
 *     `leo_tour_crm_done_v1` is missing.
 *
 * Anchoring: each step references a `data-testid` already present in the
 * app. If the anchor cannot be found (route not mounted yet, viewport too
 * small, etc.) the bubble is skipped so the tour never blocks navigation.
 *
 * Buttons: Wstecz / Dalej / Pomiń (localised). Pomiń at any point marks the
 * whole tour as done so it never re-triggers for this browser+user.
 *
 * Font is inherited from body (Inter 500 default); each bubble uses gold
 * text on a solid #171410 card with a 1px amber border and heavy backdrop.
 */

import { useEffect, useLayoutEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLocation } from "react-router-dom";

// -------- Tour definitions ------------------------------------------------

export const TOUR_STEPS = {
  dashboard: [
    {
      testid: "dash-tile-new-scene",
      title_pl: "Sceny",
      title_en: "Scenes",
      body_pl: "Przygotuj się do castingu albo dnia zdjęciowego. Tutaj pracujesz nad konkretną sceną. Wgraj tekst lub PDF, a Leo pomoże Ci przeanalizować scenę, zrozumieć postać, znaleźć jej potrzeby, stawkę i paliwo do działania oraz przygotować własną interpretację. Możesz też wykorzystać tę sekcję do nauki tekstu i prób przed castingiem lub planem. Wchodź tutaj, kiedy dostajesz scenę i chcesz być naprawdę przygotowany.",
      body_en: "Get ready for your audition or shooting day. This is where you work on a specific scene. Upload the text or a PDF, and Leo will help you analyze the scene, understand your character, discover their needs, stakes and fuel for action, and develop your own interpretation. You can also use this section to learn your lines and rehearse before an audition or a day on set. Come here when you get a scene and want to be truly prepared.",
    },
    {
      testid: "nav-leo",
      title_pl: "Leo",
      title_en: "Leo",
      body_pl: "Twój osobisty asystent aktorski. Nie wiesz, od czego zacząć? Masz pomysł, ale nie wiesz, jak zamienić go w działanie? Utknąłeś ze sceną, rolą albo swoją karierą? Porozmawiaj z Leo. Możesz wspólnie szukać inspiracji do roli, pracować nad sceną, przygotowywać się do castingu, zastanawiać się nad kolejnym krokiem kariery albo po prostu uporządkować to, co masz teraz w głowie. Wchodź tutaj, kiedy potrzebujesz drugiej głowy do myślenia.",
      body_en: "Your personal acting assistant. Not sure where to start? Have an idea but don’t know how to turn it into action? Stuck with a scene, a role, or your career? Talk to Leo. Together, you can look for inspiration for a role, work on a scene, prepare for an audition, think through your next career move, or simply organize what’s going on in your head right now. Come here when you need a second mind to think with.",
    },
    {
      testid: "dash-tile-crm",
      title_pl: "CRM",
      title_en: "CRM",
      body_pl: "Twoje relacje zawodowe mają historię. Tutaj budujesz swoją sieć kontaktów: reżyserzy castingu, reżyserzy, producenci, agenci i inni ludzie, z którymi spotykasz się zawodowo. Zapisuj spotkania, castingi, rozmowy, feedback i ważne informacje. Dzięki temu przed kolejnym castingiem możesz sprawdzić: Kiedy ostatnio się spotkaliśmy? Co wtedy robiłem? Jaki dostałem feedback? O czym rozmawialiśmy? Leo pomoże Ci również pamiętać o relacjach, które warto podtrzymywać. Wchodź tutaj nie tylko wtedy, kiedy czegoś potrzebujesz od ludzi — ale żeby świadomie budować relacje przez lata.",
      body_en: "Your professional relationships have a history. This is where you build your professional network: casting directors, directors, producers, agents, and other people you meet throughout your career. Save meetings, auditions, conversations, feedback, and important information. Before your next audition, you can come back and check: When did we last meet? What did I do then? What feedback did I receive? What did we talk about? Leo will also help you remember the relationships worth maintaining. Come here not only when you need something from people — but to consciously build relationships over the years.",
    },
    {
      testid: "dash-tile-career",
      title_pl: "Ścieżka Kariery",
      title_en: "Career Path",
      body_pl: "Zamień marzenie w plan. „Chcę grać w dobrych filmach” to marzenie. „Chcę znaleźć reprezentację w Londynie” to cel. Ale co konkretnie zrobisz w tym tygodniu, żeby się do niego zbliżyć? Tutaj określasz, dokąd chcesz dojść, dlaczego jest to dla Ciebie ważne, a następnie zamieniasz ten cel w serię małych, realnych kroków. Leo pomaga Ci stworzyć drogę od „chciałbym” do „wiem, co robię dalej”. Wchodź tutaj, kiedy chcesz świadomie kierować swoją karierą, zamiast tylko reagować na to, co przynosi branża.",
      body_en: "Turn a dream into a plan. “I want to act in great films” is a dream. “I want to find representation in London” is a goal. But what exactly are you going to do this week to move closer to it? This is where you define where you want to go, understand why it matters to you, and then turn that goal into a series of small, realistic steps. Leo helps you build a path from “I’d like to” to “I know what I’m doing next.” Come here when you want to consciously steer your career instead of simply reacting to whatever the industry brings your way.",
    },
    {
      testid: "dash-tile-ops",
      title_pl: "Zadania Operacyjne",
      title_en: "Operational Tasks",
      body_pl: "Follow-up do casting directora, aktualizacja platformy castingowej, nowe zdjęcia, wiadomość do agenta, przygotowanie self-tape’u czy wysłanie materiałów — to bieżące sprawy, o których po prostu trzeba pamiętać. Tutaj masz dostęp do zadań automatycznie wyznaczonych przez Leo, zapisujesz własne zadania, dodajesz terminy i ustawiasz przypomnienia. Wchodź tutaj, żeby mieć wszystkie bieżące sprawy zawodowe w jednym miejscu i niczego ważnego nie przegapić. Powodzenia!",
      body_en: "A follow-up with a casting director, updating your casting platform profile, new headshots, a message to your agent, preparing a self-tape, or sending out your materials — these are everyday professional tasks you simply need to remember. Here you can access tasks automatically created by Leo, add your own tasks, set deadlines, and schedule reminders. Come here to keep all your ongoing professional tasks in one place and make sure nothing important slips through the cracks. Good luck!",
    },
    {
      testid: "nav-profile",
      title_pl: "Użytkownik",
      title_en: "User",
      body_pl: "Leo dopasowany do Ciebie. Tutaj znajdziesz ustawienia swojego konta. Możesz zmienić język aplikacji oraz zdecydować, jakie powiadomienia i przypomnienia chcesz otrzymywać. Zajrzyj tutaj, kiedy chcesz zmienić sposób, w jaki Leo komunikuje się z Tobą.",
      body_en: "Leo, tailored to you. This is where you’ll find your account settings. You can change the app language and choose which notifications and reminders you want to receive. Come here whenever you want to change how Leo communicates with you.",
    },
  ],
  crm: [
    {
      testid: "crm-section-contacts",
      title_pl: "CRM → Kontakty",
      title_en: "CRM → Contacts",
      body_pl: "Buduj relacje, nie listę nazwisk. Dodawaj reżyserów castingu, reżyserzy, producentów, agentów i innych ludzi z branży. Zapisuj, gdzie się poznaliście, kiedy ostatnio mieliście kontakt i co może być ważne przy kolejnym spotkaniu. Relacje buduje się przez lata. Leo pomaga Ci o nie dbać.",
      body_en: "Build relationships, not a list of names. Add casting directors, directors, producers, agents, and other people in the industry. Keep track of where you met, when you were last in touch, and anything that might be important the next time you meet. Relationships are built over years. Leo helps you take care of them.",
    },
    {
      testid: "crm-section-auditions",
      title_pl: "CRM → Castingi",
      title_en: "CRM → Auditions",
      body_pl: "Każdy casting zostawia ślad. Zapisuj projekty, role, daty, etapy castingu i osoby, które spotykasz podczas przesłuchań. Nawet jeśli nie dostaniesz roli, ten casting może być początkiem kolejnej zawodowej relacji.",
      body_en: "Every audition leaves a trace. Keep track of projects, roles, dates, audition stages, and the people you meet along the way. Even if you don’t get the role, that audition could be the beginning of another professional relationship.",
    },
  ],
};

const STORAGE_KEY = (tourId, userId) => `leo_tour_${tourId}_done_v1__${userId || "anon"}`;

// Bilingual UI labels.
const LBL = {
  pl: { next: "Dalej", back: "Wstecz", skip: "Pomiń", finish: "Rozumiem", step: "Krok" },
  en: { next: "Next", back: "Back", skip: "Skip",  finish: "Got it",   step: "Step" },
};

// -------- Positioning ------------------------------------------------------

const BUBBLE_W = 380;   // px — bubble width when docked bottom-right
const BUBBLE_M = 20;    // gap from viewport edges

// -------- Bubble ----------------------------------------------------------

function Bubble({ step, i, total, lang, onNext, onBack, onSkip }) {
  useLayoutEffect(() => {
    const target = document.querySelector(`[data-testid="${step.testid}"]`);
    if (!target) return;
    // Scroll target roughly into view (top third of viewport).
    const r = target.getBoundingClientRect();
    if (r.top < 40 || r.bottom > window.innerHeight - 40) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    // Amber ring on the highlighted target for as long as the bubble is up.
    target.classList.add("leo-tour-highlight");
    return () => target.classList.remove("leo-tour-highlight");
  }, [step.testid]);

  const lbl = LBL[lang === "en" ? "en" : "pl"];
  const isLast = i === total - 1;

  return (
    <>
      {/* Dim + block interactions with the rest of the app */}
      <div className="leo-tour-backdrop" onClick={onSkip} data-testid="tour-backdrop" />
      <div
        className="leo-tour-bubble"
        style={{ width: `${BUBBLE_W}px`, right: `${BUBBLE_M}px`, bottom: `${BUBBLE_M}px` }}
        data-testid={`tour-bubble-${i + 1}`}
        role="dialog"
        aria-modal="true"
      >
        <div className="leo-tour-eyebrow">
          {lbl.step} {i + 1} / {total}
        </div>
        <div className="leo-tour-title">
          {lang === "en" ? step.title_en : step.title_pl}
        </div>
        <div className="leo-tour-body">
          {lang === "en" ? step.body_en : step.body_pl}
        </div>
        <div className="leo-tour-actions">
          <button
            type="button"
            className="leo-tour-btn leo-tour-btn-ghost"
            onClick={onSkip}
            data-testid="tour-skip-btn"
          >
            {lbl.skip}
          </button>
          <div className="flex-1" />
          {i > 0 && (
            <button
              type="button"
              className="leo-tour-btn leo-tour-btn-ghost"
              onClick={onBack}
              data-testid="tour-back-btn"
            >
              {lbl.back}
            </button>
          )}
          <button
            type="button"
            className="leo-tour-btn leo-tour-btn-primary"
            onClick={onNext}
            data-testid="tour-next-btn"
          >
            {isLast ? lbl.finish : lbl.next}
          </button>
        </div>
      </div>
    </>
  );
}

// -------- Root controller ------------------------------------------------

export default function GuidedTour() {
  const { user, lang } = useAuth();
  const location = useLocation();
  const [tourId, setTourId] = useState(null);      // "dashboard" | "crm" | null
  const [stepIdx, setStepIdx] = useState(0);

  const userId = user?.user_id;

  // Autostart on first visit to /dashboard or /crm.
  useEffect(() => {
    if (!userId) return;
    let candidate = null;
    if (location.pathname.startsWith("/dashboard")) candidate = "dashboard";
    else if (location.pathname.startsWith("/crm")) candidate = "crm";
    if (!candidate) return;
    const done = typeof window !== "undefined" && window.localStorage.getItem(STORAGE_KEY(candidate, userId));
    if (done) return;
    // Delay slightly so target components render + measure correctly.
    const timer = setTimeout(() => {
      setTourId(candidate);
      setStepIdx(0);
    }, 600);
    return () => clearTimeout(timer);
  }, [location.pathname, userId]);

  // While a tour is up, mark <body> so the app header can lift itself above
  // the dim backdrop (see `.leo-tour-active header` in index.css). This lets
  // the actor keep seeing the nav labels — the tour becomes contextual, not
  // a full-screen modal. Declared BEFORE any early return to satisfy the
  // rules-of-hooks constraint.
  useEffect(() => {
    if (tourId) {
      document.body.classList.add("leo-tour-active");
      return () => document.body.classList.remove("leo-tour-active");
    }
  }, [tourId]);

  if (!tourId) return null;
  const steps = TOUR_STEPS[tourId] || [];
  if (steps.length === 0) return null;

  // Skip a step whose anchor doesn't exist on the current page (e.g. mobile
  // hides the nav bar). If NONE of the remaining steps has a target, close.
  const activeStep = (() => {
    for (let i = stepIdx; i < steps.length; i++) {
      if (document.querySelector(`[data-testid="${steps[i].testid}"]`)) {
        if (i !== stepIdx) setStepIdx(i);
        return steps[i];
      }
    }
    return null;
  })();

  const closeAndPersist = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY(tourId, userId), "1");
    } catch {}
    // Notify listeners (e.g. ActivationChoice modal) that the guided tour
    // has finished for the first time. Existing users who already finished
    // the tour before this feature landed will NOT receive this event.
    try { window.dispatchEvent(new CustomEvent("leo:tour_finished", { detail: { tourId } })); } catch {}
    setTourId(null);
  };

  if (!activeStep) {
    // Deferred close on next tick to avoid setState-in-render warning.
    setTimeout(closeAndPersist, 0);
    return null;
  }

  const onNext = () => {
    if (stepIdx >= steps.length - 1) closeAndPersist();
    else setStepIdx(stepIdx + 1);
  };
  const onBack = () => setStepIdx(Math.max(0, stepIdx - 1));

  return (
    <Bubble
      step={activeStep}
      i={stepIdx}
      total={steps.length}
      lang={lang}
      onNext={onNext}
      onBack={onBack}
      onSkip={closeAndPersist}
    />
  );
}
